<template>
    <nav class="main-navigation" :class="bemFacets">
        <div :style="panelStyle" class="main-navigation--panel" :class="panelClasses"/>

        <ul class="main-navigation--row u-reset"
                ref="menu"
                @mouseleave="onContainerMouseLeave"
                @mouseenter="onContainerMouseEnter">
            <li class="main-navigation--trigger"
                    tabindex="0"
                    v-for="(item, index) in items"
                    :key="index"
                    @focus="setActiveIndex(index)"
                    @mouseleave="setActiveIndex(null)"
                    @mouseenter="setActiveIndex(index)">
                <conditional-link class="main-navigation--link main-navigation--link__parent u-reset"
                        :class="getLinkClasses(item)"
                        :path="item.path"
                        :label="item.label"/>
                <ul class="main-navigation--list u-reset" :class="getNavigationListClasses(item, index)">
                    <li class="main-navigation--item"
                            :class="getItemClasses(child)"
                            v-for="(child, j) in item.children"
                            :key="j">
                        <img class="main-navigation--image"
                                :src="child.image"
                                v-if="child.image"
                                @mouseenter="setActiveIndex(null)">
                        <a :class="getLinkClasses(child)"
                                class="main-navigation--link main-navigation--link__child u-reset"
                                :href="child.path"
                                @blur="onLastItemBlur(j, item.children)">
                            {{ child.label }}
                        </a>
                    </li>
                </ul>
            </li>
        </ul>

        <hr class="main-navigation--line"
                :class="lineClasses"
                :style="lineStyle"
                v-if="isMounted"
                ref="line">
    </nav>
</template>

<script>
    /* eslint-disable camelcase */
    import { bemMixin, isMountedMixin } from '@dreipol/vue-ui/src/mixins';
    import { isNil, debounce } from 'lodash/fp';
    import $ from 'bianco.query';
    import ConditionalLink from '../../components/conditional-link/conditional-link';
    import { mapState } from 'vuex';

    const panelDebounce = debounce(50);

    export default {
        components: { ConditionalLink },
        mixins: [
            isMountedMixin,
            bemMixin('main-navigation'),
        ],
        props: {
            items: {
                type: Array,
                required: true,
            },
        },
        data() {
            return {
                panelStyle: {
                    opacity: 0,
                },
                isPanelActive: false,
                isLineAnimationEnabled: false,
                activeIndex: null,
            };
        },
        computed: {
            ...mapState('viewport', {
                viewportWidth: 'width',
            }),
            panelClasses() {
                return [
                    this.bemAdd(this.isPanelActive ? 'active' : 'hidden', 'panel'),
                ];
            },
            lineClasses() {
                return [
                    this.bemAdd(this.isLineAnimationEnabled ? 'has-translate-animation' : 'base', 'line'),
                ];
            },
            lineStyle() {
                // recompute this function once this component will be mounted
                // and when the viewport width will change
                this.isMounted && this.viewportWidth; // eslint-disable-line

                const { menu } = this.$refs;
                const activeTrigger = menu && menu.children[this.activeLinkIndex];

                if (activeTrigger) {
                    return {
                        width: `${ activeTrigger.offsetWidth }px`,
                        opacity: this.hasActiveMenuLink ? 1 : 0,
                        transform: `translate(${ activeTrigger.offsetLeft }px, 0)`,
                    };
                }

                return {};
            },
            menuLists() {
                // recompute this function once this component will be mounted
                this.isMounted; // eslint-disable-line
                const { menu } = this.$refs;

                return $('.main-navigation--list', menu);
            },
            activeMenuList() {
                return this.menuLists.length ? this.menuLists[this.activeIndex] : null;
            },
            isMenuPanelVisible() {
                return this.isMenuDisabled ? false : this.areChildrenVisible(this.items[this.activeIndex], this.activeIndex);
            },
            isMenuDisabled() {
                return isNil(this.activeIndex);
            },
            hasActiveMenuLink() {
                return !isNil(this.activeLinkIndex) && this.activeLinkIndex > -1;
            },
            activeLinkIndex() {
                return this.isMenuDisabled ? this.items.findIndex(({ is_active }) => is_active) : this.activeIndex;
            },
        },
        methods: {
            onLastItemBlur(index, children) {
                return index === children.length - 1 ? this.setActiveIndex(null) : null;
            },
            getNavigationListClasses(item, index) {
                return [
                    this.bemAdd(this.areChildrenVisible(item, index) ? 'visible' : 'hidden', 'list'),
                ];
            },
            getItemClasses(item) {
                return item.facets.map(facet => this.bemAdd(facet, 'item'));
            },
            getLinkClasses(link) {
                return [this.bemAdd(link.is_active ? 'active' : 'base', 'link')];
            },
            areChildrenVisible(item, index) {
                return this.activeIndex === index && item.children && item.children.length;
            },
            setPanelStyle() {
                const { transform } = this.lineStyle;
                const isPanelActive = this.activeMenuList && this.isMenuPanelVisible;

                this.panelStyle = isPanelActive ? {
                    opacity: 1,
                    width: `${ this.activeMenuList.offsetWidth }px`,
                    height: `${ this.activeMenuList.offsetHeight }px`,
                    transform,
                } : {
                    ...this.panelStyle,
                    opacity: 0,
                    boxShadow: 'none',
                };

                this.debouncedPanelActivation(isPanelActive);
            },
            onContainerMouseLeave() {
                if (!this.hasActiveMenuLink) {
                    this.toggleLineAnimationEnabled(false);
                }
            },
            onContainerMouseEnter() {
                if (this.hasActiveMenuLink) {
                    this.toggleLineAnimationEnabled(true);
                } else {
                    this.debounceLineAnimationActivation(true);
                }
            },
            toggleLineAnimationEnabled(isEnabled) {
                this.isLineAnimationEnabled = isEnabled;
            },
            debouncedPanelActivation: panelDebounce(function(isPanelActive) {
                this.isPanelActive = isPanelActive;
            }),
            debouncedPanelUpdate: panelDebounce(function() {
                this.setPanelStyle();
            }),
            debounceLineAnimationActivation: panelDebounce(function(isEnabled) {
                this.toggleLineAnimationEnabled(isEnabled);
            }),
            setActiveIndex(index) {
                this.activeIndex = index;
                this.debouncedPanelUpdate(index);
            },
        },
    };
</script>
